import { Button } from '@realtyka/real-ui';
import { isEmpty } from 'lodash';
import React, { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import useMediaQuery from '../../../../hooks/useMediaQuery';
import {
  AnswerApplicationQuestionRequest,
  FieldObjectTypeEnum,
  Question,
  SectionIntro,
} from '../../../../openapi/atlantis';
import { getSafeAreaPadding } from '../../../../utils/DeviceDetectionUtils';
import { OnboardingResponseRoleEnum } from '../../../../openapi/yenta';
import { useAppSelector } from '../../../../slices/store';
import { cn } from '../../../../utils/classUtils';
import {
  ApplicationStepWithNewContent,
  getOnboardingPreviousQuestionId,
  getPreviousQuestionId,
} from '../../../../utils/ConfigDrivenFormUtils';
import { getLegacyApplicationId } from '../../../../utils/OnboardingHelpers';
import { isSectionIntro } from '../../../../utils/TypeUtils';

interface ConfigDrivenFormFooterProps {
  config: ApplicationStepWithNewContent<Question | SectionIntro>;
  onNavigateToQuestion?: (questionId: string) => Promise<void>;
  onSubmit: (e: AnswerApplicationQuestionRequest) => void;
  applicationId?: string;
}

const ConfigDrivenFormFooter: React.FC<ConfigDrivenFormFooterProps> = ({
  config,
  onNavigateToQuestion,
  onSubmit,
  applicationId,
}) => {
  const {
    application: { progress },
    auth: { authUserDetail },
  } = useAppSelector((state) => state);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const {
    formState: { isValid, errors, isSubmitting },
    setValue,
    handleSubmit,
  } = useFormContext<AnswerApplicationQuestionRequest>();
  const isMobile = useMediaQuery('(max-width: 640px)');

  const CTAField = useMemo(() => {
    if (
      isSectionIntro(config) &&
      (config?.content as SectionIntro)?.cta?.type ===
        FieldObjectTypeEnum.Button
    ) {
      return (config?.content as SectionIntro)?.cta;
    }

    const nextButtonField = (config?.content as Question)?.fields?.find(
      (field) => field.type === FieldObjectTypeEnum.Button,
    );

    return nextButtonField;
  }, [config]);

  const prevQuestionId = useMemo(
    () =>
      applicationId
        ? getOnboardingPreviousQuestionId(
            progress?.sectionProgresses || [],
            config.id,
          )
        : getPreviousQuestionId(
            progress?.sectionProgresses || [],
            config.content.id,
          ),
    [applicationId, config.content.id, config.id, progress?.sectionProgresses],
  );

  const handleOnPrev = async (questionId: string) => {
    setIsLoading(true);
    await onNavigateToQuestion!(questionId);
    setIsLoading(false);
  };

  const isAgentOnboardingWithApplicationId = getLegacyApplicationId(
    authUserDetail,
    OnboardingResponseRoleEnum.Agent,
  );

  if (!prevQuestionId && !CTAField) {
    return null;
  }

  return (
    <div
      className={cn(
        'absolute bottom-0 left-0 right-0 bg-white border-t border-reskin-grey-300',
      )}
      style={{
        paddingBottom: getSafeAreaPadding(isMobile),
      }}
    >
      <div
        className={cn('flex flex-row items-center justify-between px-8 py-4', {
          'justify-end': !prevQuestionId,
        })}
      >
        {!!prevQuestionId && !isMobile && (
          <Button
            onClick={() => handleOnPrev(prevQuestionId)}
            disabled={isLoading}
            type='button'
            variant='outline'
            color='secondary'
          >
            <span className='px-3.5'>
              {!!isAgentOnboardingWithApplicationId ? 'Back' : 'Previous'}
            </span>
          </Button>
        )}

        {!!CTAField && (
          <Button
            onClick={async () => {
              setValue(CTAField?.id! as `answers.${number}.answer`, true);
              await handleSubmit(onSubmit)();
            }}
            disabled={!isValid || !isEmpty(errors) || isSubmitting}
            type='button'
            variant='filled'
            color='primary'
            fullWidth={isMobile}
          >
            <span className='px-6'>{CTAField?.label}</span>
          </Button>
        )}
      </div>
    </div>
  );
};

export default ConfigDrivenFormFooter;
